import React, { useEffect, useMemo, useState } from "react";
import { Alert, Col, Form, Row, Spinner } from "react-bootstrap";
import QRCode from "qrcode";
import DashboardLayout from "../components/DashboardLayout";
import CertificateAssetPicker from "../components/CertificateAssetPicker";
import CertificateDocument from "../components/CertificateDocument";
import FooterEditor, { footerToLogos } from "../components/FooterEditor";
import { PREVIEW_STORAGE_KEY } from "./CertificatePreviewPage";
import { useAuth } from "../context/userAuth/UserAuthState";
import {
  createCertificateAssetAPI,
  fetchCertificateConfigAPI,
  fetchCertificateTemplatesAPI,
  fetchCertificationByIdAPI,
  generateCertificateAPI,
  saveCertificateFooterAPI,
} from "../api/api";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BsArrowLeft, BsBoxArrowUpRight, BsFileEarmarkPdf, BsPlus, BsSave, BsSliders, BsTrash, BsUpload } from "react-icons/bs";

const DEFAULT_PUBLIC_SITE_URL = "https://verification.tuvpak.com";

const SHARED_DISCLAIMER =
  "This Report does not make any warranty, expressed or implied, concerning the above equipment. This Report does not relieve seller/suppliers from their contractual responsibilities with regard to quality/quantity of this delivery nor does it prejudice the buyer’s right of claim towards their seller/suppliers for compensation for any apparent and/or hidden defects not detected during our inspection or occurring thereafter";
const SHARED_END_LINE = "********************************************** END OF REPORT **********************************************";

// Defaults mirror the supplied "Assessment Report - Aluminum Truss" PDF.
const defaultComponentRows = [
  { component: "Structural Steel Beam", assessment_item: "Verify beam section, span & condition adequate for additional suspended point load", requirement: "Adequate residual capacity for design load + existing loads", design_load: "1000 Kg’s", status: "Satisfactory" },
  { component: "Beam Clamp", assessment_item: "Suitable for beam flange width/thickness; correctly seated & secured", requirement: "WLL ≥ design load; mfr. instructions followed", design_load: "1000 Kg’s", status: "Satisfactory" },
  { component: "D-Shackle", assessment_item: "Correct size/type; pin fully engaged; free of damage/deformation", requirement: "WLL ≥ design load; certified", design_load: "1000 Kg’s", status: "Satisfactory" },
  { component: "Chain Sling with Hook", assessment_item: "Correct grade/length/angle; free of wear, corrosion, defects", requirement: "WLL ≥ design load; certified", design_load: "2000 Kg’s", status: "Satisfactory" },
  { component: "Chain Block", assessment_item: "Compatible with truss chord profile; rated for overhead suspension", requirement: "WLL ≥ design load; certified", design_load: "2000 Kg’s", status: "Satisfactory" },
  { component: "Lifting Belt", assessment_item: "Correct grade/length/angle; free of wear & tear, defects", requirement: "WLL ≥ design load; certified", design_load: "2000 Kg’s", status: "Satisfactory" },
  { component: "Aluminum Truss", assessment_item: "Load path continuity, alignment, angles, secondary retention", requirement: "Complete verified load path, beam-to-truss", design_load: "240 Kg’s", status: "Satisfactory" },
];

const emptyRow = { component: "", assessment_item: "", requirement: "", design_load: "", status: "" };

// Fallback footer if no saved footer exists yet (the backend seeds one).
const DEFAULT_FOOTER_COMPANIES = [
  { key: "astm", name: "ASTM International", url: "https://www.astm.org/", asset_id: "" },
  { key: "asnt", name: "ASNT", url: "https://www.asnt.org/", asset_id: "" },
  { key: "aws", name: "AWS", url: "https://www.aws.org/", asset_id: "" },
  { key: "twi", name: "TWI", url: "https://www.twi-global.com/", asset_id: "" },
  { key: "leea", name: "LEEA", url: "https://leeaint.com/", asset_id: "" },
  { key: "iso", name: "NQA ISO 9001", url: "https://www.nqa.com/", asset_id: "" },
  { key: "ukas", name: "UKAS", url: "https://www.ukas.com/", asset_id: "" },
];

const sharedSignOffDefaults = {
  inspected_by_name: "Fakhar Naeem",
  inspected_by_signature_id: "",
  approved_by_name: "Shahbaz Ahmed Chohan",
  approved_by_signature_id: "",
  company_seal_id: "",
  disclaimer_text: SHARED_DISCLAIMER,
  end_of_report_text: SHARED_END_LINE,
  footer_id: "",
  footer_companies: DEFAULT_FOOTER_COMPANIES.map((company) => ({ ...company })),
  footer_website: "www.tuvpak.com",
  footer_email: "info@tuvpak.com",
  footer_gmail: "tuvinspection.pk@gmail.com",
};

const aluminumDefaults = {
  issue_date: "2026-09-15",
  expire_date: "",
  report_title: "SUSPENSION / HANGING SAFETY ASSESSMENT REPORT",
  project: "Cherry Event",
  report_no: "TUV-BL-SAR-001",
  location: "Hall No. 1",
  report_date: "2026-09-15",
  client: "Brand Logic",
  contractor: "N/A",
  structure: "Alum Truss (240 kg)",
  revision: "00",
  purpose_scope:
    "This report presents a preliminary assessment of the proposed suspension arrangement for a temporary aluminum truss at an expo/event venue. It reviews the suspension load path and lists verification and safety requirements.",
  proposed_suspension_arrangement:
    "Existing Steel Beam → Beam Clamp → D-Shackle → Chain Sling → Truss Clamp → Aluminum Truss",
  load_calculation:
    "Reported truss weight: 240 kg.  Static load = 240 kg × 9.81 m/s² = **2.35 kN**.  This 2.35 kN figure is the truss self-weight only.",
  component_rows: defaultComponentRows,
  final_assessment:
    "**Conclusion:** Based on the reported total weight of the aluminum truss structure, approximately 240 kg (2.35 kN static load), and based on the assessment and verification carried out by the Structural Engineer, the existing structural beam/supporting structure has been assessed as structurally adequate and capable of safely supporting the proposed aluminum truss load. The proposed suspension arrangement, consisting of the beam clamp, D-shackle, chain sling, and truss clamp, is considered suitable for the intended temporary application, subject to the use of properly certified components with adequate WLL/SWL, correct installation, and compliance with the manufacturers' requirements and applicable event/rigging safety requirements. Accordingly, based on the Structural Engineer's assessment, the existing structure is considered safe and sufficient to support the 240 kg aluminum truss structure under the proposed suspension arrangement.",
  ...sharedSignOffDefaults,
};

const craneDefaults = {
  issue_date: "2026-05-05",
  expire_date: "2027-05-04",
  report_title: "THOROUGH EXAMINATION REPORT",
  report_no: "TUV/ZKB/INSP/TP/040526-001",
  activity: "Certification of Mobile Crane",
  client: "M/S ZKB Engineers & Constructors.",
  location: "Karachi",
  description_of_supply: "Certification of Mobile Crane",
  make_model: "TADANO",
  serial_no: "FB2187",
  registration_number: "197731",
  capacity: "25 Ton",
  type_of_operation: "Lifting",
  scope_of_survey: "Visual Inspection and Functional Test",
  scope_of_survey_detail:
    "1. Visual Examination/Inspection, Inspection and Verification of Mechanical Engine / Hydraulic System, Hoist, Horns, Boom Stopper, Out Rigger, Anti-Two Block and Air Cooling System, Chassis, Engine Hood, Gear Box, Spring and Shock, Oil Tank, Clutches, Steering Control, Valves, Battery, Electrical System, Power Test, Transmission Manual, Brake/Park Brake, Tires Condition Reverse Indications, Warning Light, Flame Arrestor, Wipers, Fire Extinguisher Validity, and Driver Protection\n\n2. Function Test: Found Satisfactory",
  operation_test: "Satisfactory",
  date_of_issuance: "2026-05-05",
  date_of_validity: "2027-05-04",
  date_of_inspection: "2026-05-05",
  report_date: "2026-05-05",
  reference_code: "ISO –6405.2:2023",
  recommendations: "Found Satisfactory",
  exam_photo_id: "",
  ...sharedSignOffDefaults,
};

const FM200_SHELL_KEYS = [
  "coating", "corrosion", "dents", "cuts_dig", "leaks",
  "fire_damage", "bulges", "neck_defects", "attachments",
];
const FM200_SHELL_LABELS = [
  "Coating", "Corrosion", "Dents", "Cuts, Dig", "Leaks",
  "Fire Damage", "Bulges", "Neck Defects", "Attachments",
];

const emptyCylinderRow = () => ({
  identification_no: "",
  icc_dot_spec: "",
  mfg: "",
  date_of_mfg: "",
  size_t_weight: "",
  coating: true,
  corrosion: true,
  dents: true,
  cuts_dig: true,
  leaks: true,
  fire_damage: true,
  bulges: true,
  neck_defects: true,
  attachments: true,
  disposition: "OK",
  inspection_date: "",
  remarks: "PASS",
});

const defaultCylinderRows = [
  {
    ...emptyCylinderRow(),
    identification_no: "AA337985",
    icc_dot_spec: "4BW-500",
    mfg: "--",
    date_of_mfg: "--",
    size_t_weight: "150 kg",
    inspection_date: "2026-01-14",
  },
  {
    ...emptyCylinderRow(),
    identification_no: "AA297806",
    icc_dot_spec: "4BW-500",
    mfg: "--",
    date_of_mfg: "--",
    size_t_weight: "150 kg",
    inspection_date: "2026-01-14",
  },
  {
    ...emptyCylinderRow(),
    identification_no: "AA555785",
    icc_dot_spec: "4BW-500",
    mfg: "--",
    date_of_mfg: "--",
    size_t_weight: "25 kg",
    inspection_date: "2026-01-14",
  },
];

const fm200Defaults = {
  issue_date: "2026-01-14",
  expire_date: "2026-07-13",
  report_title: "FM 200 GAS CYLINDERS PERIODIC VISUAL INSPECTION & HYDROSTATIC TEST REPORT",
  report_no: "INCRT-140126-001",
  company: "HITECH SAFETY CORPORATION",
  client: "OMYA PACK PVT. LTD.",
  report_date: "2026-01-14",
  note:
    "The above-mentioned cylinder identification number was hydrostatically tested at a test pressure of 38 bar. The cylinder was found satisfactory, with no leakage or abnormal deformation observed, and the hydrostatic test was successfully passed.",
  cylinder_rows: defaultCylinderRows,
  ...sharedSignOffDefaults,
  inspected_by_name: "Muhammad Muzamal",
};

const emptyEmissionRow = () => ({
  parameter: "",
  unit: "",
  methodology: "",
  test_result: "",
  neqs: "",
});

const defaultEmissionRows = [
  { parameter: "Smoke", unit: "Ringlemann Scale", methodology: "ASTM D-2156", test_result: "1", neqs: "2" },
  { parameter: "Carbon Monoxide", unit: "CO (mg/Nm3)", methodology: "ASTM D-6522", test_result: "526", neqs: "800" },
  { parameter: "Sulphur Dioxide", unit: "SO2 (mg/Nm3)", methodology: "ASTM D-6522", test_result: "55", neqs: "1700" },
  { parameter: "Oxides of Nitrogen", unit: "NOx (mg/Nm3)", methodology: "ASTM D-6522", test_result: "487", neqs: "600" },
  { parameter: "Particulate Matter", unit: "PM (mg/Nm3)", methodology: "ASTM D-3685", test_result: "49", neqs: "300" },
  { parameter: "Noise (at 7.5m Distance)", unit: "dB(A)", methodology: "ASTM E-1124", test_result: "81", neqs: "85" },
];

const emissionDefaults = {
  issue_date: "2026-04-20",
  expire_date: "2027-01-08",
  report_title: "EMISSION TEST REPORT",
  equipment_type: "Diesel Power Generator",
  report_no: "TUV-HTMA-EMT-200426-002",
  client: "M/S HTMA Pvt. Ltd.",
  model: "LEROY SOMER / LSA 42.2S5 J 6/4",
  serial_no: "HP35107U932943S",
  manufacturer: "LEROY SOMER",
  capacity: "20 kW",
  nature_of_power: "Electrical",
  fuel_type: "Diesel",
  emission_standard: "Pre – Euro 0",
  location: "Chaghi, Baluchistan",
  date_of_test: "2026-04-20",
  ambient_temperature: "35 °C",
  humidity: "60% RH",
  test_methodology:
    "Test was conducted using a Free Acceleration Test as per ISO 8178 / ASTM",
  emission_rows: defaultEmissionRows,
  smoke_meter: "AVL DISmoke 480",
  calibration_date: "2026-01-09",
  calibration_validity: "2027-01-08",
  meter_serial_no: "TUV- SM-001 / 236578DE2",
  remarks_conclusion:
    "The Diesel Generator Passed the emission test as per the applicable standards for in use diesel engine. The Generator is considered complaint for operation under the current environment regulation",
  ...sharedSignOffDefaults,
  inspected_by_name: "Muhammad Muzamal",
};

const emptyAccessoryRow = () => ({
  description: "",
  wll_swl: "",
  date_of_examination: "",
  date_of_next_examination: "",
  type_of_inspection: "Visual",
  identification: "",
  qty: "01",
  safe_to_use: "YES",
});

const defaultAccessoryRows = [
  {
    ...emptyAccessoryRow(),
    description: "Lifting Clamp",
    wll_swl: "2 Ton",
    date_of_examination: "2026-09-15",
    date_of_next_examination: "2027-03-14",
    identification: "26013145",
  },
  {
    ...emptyAccessoryRow(),
    description: "Webbing Sling",
    wll_swl: "2 Ton",
    date_of_examination: "2026-09-15",
    date_of_next_examination: "2027-03-14",
    identification: "211115-1108A",
  },
  {
    ...emptyAccessoryRow(),
    description: "Chain Block",
    wll_swl: "2 Ton",
    date_of_examination: "2026-09-15",
    date_of_next_examination: "2027-03-14",
    identification: "BL-CB-001",
  },
];

const liftingDefaults = {
  issue_date: "2026-09-15",
  expire_date: "2027-03-14",
  report_title: "LIFTING ACCESSORIES REGISTER",
  report_no: "TUV-BL-LAR-150926-002",
  client: "Brand Logic",
  job_no: "150926-001",
  place_of_inspection: "TUV Pak Lab",
  standard_specification: "As per manufacturer manual",
  registration_no: "N/A",
  inspection_standard: "OSHA 1910.140",
  conclusion_remarks:
    "LIFTING ACCESSORIES WAS FOUND IN ACCEPTABLE CONDITION AND SAFE FOR CONVENTIONAL OPERATIONS.",
  remarks: "VALIDITY OF THIS REPORT IS 06 MONTH FROM THE DATE OF INSPECTION.",
  accessory_rows: defaultAccessoryRows,
  ...sharedSignOffDefaults,
};

const emptyTestResultRow = () => ({
  parameter: "",
  test_method: "",
  unit: "",
  result: "",
  applicable_limit: "",
  compliance: "N/A",
});

const defaultTestResultRows = [
  { parameter: "pH", test_method: "[Standard to be confirmed]", unit: "—", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
  { parameter: "Temperature", test_method: "[Standard to be confirmed]", unit: "°C", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
  { parameter: "Total Suspended Solids (TSS)", test_method: "[Standard to be confirmed]", unit: "mg/L", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
  { parameter: "BOD₅ (5-day)", test_method: "[Standard to be confirmed]", unit: "mg/L", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
  { parameter: "Chemical Oxygen Demand (COD)", test_method: "[Standard to be confirmed]", unit: "mg/L", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
  { parameter: "Oil & Grease", test_method: "[Standard to be confirmed]", unit: "mg/L", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
  { parameter: "Total Nitrogen", test_method: "[Standard to be confirmed]", unit: "mg/L", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
  { parameter: "Total Phosphorus", test_method: "[Standard to be confirmed]", unit: "mg/L", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
  { parameter: "Ammonia (as N)", test_method: "[Standard to be confirmed]", unit: "mg/L", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
  { parameter: "Fecal Coliform / E. coli", test_method: "[Standard to be confirmed]", unit: "CFU/100 mL", result: "To be determined", applicable_limit: "Applicable standard/limit to be confirmed", compliance: "N/A" },
];

const wastewaterDefaults = {
  issue_date: "2026-09-01",
  expire_date: "2027-09-01",
  report_title: "WASTEWATER / LIQUID EFFLUENT QUALITY TEST REPORT",
  report_no: "HTMA-LEQT-250826-001",
  company_name: "HTMA Pvt Ltd",
  project: "Site Kis-8",
  client: "",
  location: "Chagai Balochistan",
  date_of_sampling: "2026-08-30",
  date_of_testing: "2026-08-31",
  report_date: "2026-09-01",
  sample_id: "",
  sample_type: "Wastewater / Liquid Effluent",
  sampling_location: "",
  sampling_method: "",
  sample_condition: "",
  sampling_datetime: "",
  test_result_rows: defaultTestResultRows,
  compliance_statement:
    "Based on the above laboratory test results, the tested wastewater / liquid effluent sample has been evaluated against the applicable project, client, or regulatory requirements. The sample is determined to be COMPLIANT with the specified requirements.",
  ...sharedSignOffDefaults,
  inspected_by_name: "Muhammad Muzamil",
};

const craneOperatorDefaults = {
  issue_date: "2026-05-04",
  expire_date: "2027-05-03",
  report_title: "CRANE OPERATOR",
  report_no: "TAC25067",
  holder_name: "Mohammed Dilawar",
  id_no: "3610228720615",
  company: "ZKB Engineers & Constructors",
  project: "N/A",
  level: "N/A",
  trained_by_name: "Muhammad Muzamal",
  approved_by_name: "Shahbaz Ahmed Chohan",
  exam_photo_id: "",
  ...sharedSignOffDefaults,
  inspected_by_name: "Muhammad Muzamal",
};

const defaultsForTemplate = (templateId) => {
  const base = templateId === "thorough_examination_crane"
    ? craneDefaults
    : templateId === "fm200_gas_cylinder"
      ? fm200Defaults
      : templateId === "emission_test_diesel_generator"
        ? emissionDefaults
        : templateId === "lifting_accessories_register"
          ? liftingDefaults
          : templateId === "wastewater_effluent_quality"
            ? wastewaterDefaults
            : templateId === "crane_operator_card"
              ? craneOperatorDefaults
              : aluminumDefaults;
  return {
    ...base,
    component_rows: Array.isArray(base.component_rows)
      ? base.component_rows.map((row) => ({ ...row }))
      : undefined,
    cylinder_rows: Array.isArray(base.cylinder_rows)
      ? base.cylinder_rows.map((row) => ({ ...row }))
      : undefined,
    emission_rows: Array.isArray(base.emission_rows)
      ? base.emission_rows.map((row) => ({ ...row }))
      : undefined,
    accessory_rows: Array.isArray(base.accessory_rows)
      ? base.accessory_rows.map((row) => ({ ...row }))
      : undefined,
    test_result_rows: Array.isArray(base.test_result_rows)
      ? base.test_result_rows.map((row) => ({ ...row }))
      : undefined,
    footer_companies: (base.footer_companies || []).map((company) => ({ ...company })),
  };
};

const defaultForm = aluminumDefaults;

// Form fields taken from a saved footer set.
const footerFields = (footer) => ({
  footer_id: footer.id,
  footer_companies: (footer.companies || []).map((company) => ({ ...company })),
  footer_website: footer.website || "",
  footer_email: footer.email || "",
  footer_gmail: footer.gmail || "",
});

// Form fields taken from the shared configuration defaults
// (Inspected By, Approved By, Company Seal and footer).
const defaultsToFields = (config) => {
  if (!config) return {};
  const { assets = [], footers = [], defaults = {} } = config;
  const byId = (id) => assets.find((asset) => String(asset.id) === String(id));
  const inspected = byId(defaults.inspected_by_asset_id);
  const approved = byId(defaults.approved_by_asset_id);
  const seal = byId(defaults.company_seal_asset_id);
  const footer = footers.find((item) => String(item.id) === String(defaults.footer_id)) || footers[0];
  return {
    inspected_by_signature_id: inspected?.id || "",
    inspected_by_name: inspected?.name || sharedSignOffDefaults.inspected_by_name,
    approved_by_signature_id: approved?.id || "",
    approved_by_name: approved?.name || sharedSignOffDefaults.approved_by_name,
    company_seal_id: seal?.id || "",
    ...(footer ? footerFields(footer) : {}),
  };
};

const CreateCertificatePage = () => {
  const { user } = useAuth();
  const token = user?.authorization;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editIdParam = searchParams.get("id");
  const [editingId, setEditingId] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(Boolean(editIdParam));
  const [templates, setTemplates] = useState([]);
  const [publicSiteUrl, setPublicSiteUrl] = useState(DEFAULT_PUBLIC_SITE_URL);
  const [assets, setAssets] = useState([]);
  const [footers, setFooters] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingFooter, setSavingFooter] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const isCrane = selectedTemplate?.id === "thorough_examination_crane";
  const isFm200 = selectedTemplate?.id === "fm200_gas_cylinder";
  const isEmission = selectedTemplate?.id === "emission_test_diesel_generator";
  const isLifting = selectedTemplate?.id === "lifting_accessories_register";
  const isWastewater = selectedTemplate?.id === "wastewater_effluent_quality";
  const isCraneOperator = selectedTemplate?.id === "crane_operator_card";
  const noDisclaimer = isFm200 || isEmission || isCraneOperator;
  const isCardTemplate = isCraneOperator;
  const signatures = useMemo(() => assets.filter((asset) => asset.asset_type === "signature"), [assets]);
  const seals = useMemo(() => assets.filter((asset) => asset.asset_type === "seal"), [assets]);
  const footerAssets = useMemo(() => assets.filter((asset) => asset.asset_type === "footer"), [assets]);
  const assetById = (id) => assets.find((asset) => String(asset.id) === String(id));

  const openTemplate = (template) => {
    if (editingId) return;
    setSelectedTemplate(template);
    setForm({
      ...defaultsForTemplate(template.id),
      ...defaultsToFields(config),
    });
    setMessage("");
    setError("");
  };

  useEffect(() => {
    const load = async () => {
      const [templateRes, configRes] = await Promise.all([
        fetchCertificateTemplatesAPI(token),
        fetchCertificateConfigAPI(token),
      ]);
      let nextTemplates = [];
      let nextConfig = null;
      let nextAssets = [];
      if (templateRes.success) {
        nextTemplates = templateRes.templates || [];
        setTemplates(nextTemplates);
        if (templateRes.public_site_url) setPublicSiteUrl(templateRes.public_site_url);
      } else {
        setError(templateRes.message || "Failed to load templates.");
      }
      if (configRes.success) {
        nextAssets = configRes.assets || [];
        nextConfig = configRes;
        setAssets(nextAssets);
        setFooters(configRes.footers || []);
        setConfig(configRes);
        setForm((prev) => ({ ...prev, ...defaultsToFields(configRes) }));
      } else {
        setError(configRes.message || "Failed to load certificate configuration.");
      }

      if (editIdParam) {
        setLoadingEdit(true);
        const certRes = await fetchCertificationByIdAPI({ id: editIdParam }, token);
        if (!certRes.success) {
          setError(certRes.message || "Failed to load certificate for editing.");
          setLoadingEdit(false);
          return;
        }
        const cert = certRes.certification;
        const template = nextTemplates.find((item) => item.id === cert.template_id);
        if (!template) {
          setError("Template for this certificate is no longer available.");
          setLoadingEdit(false);
          return;
        }
        const saved = cert.template_data || {};
        setEditingId(cert.id);
        setSelectedTemplate(template);
        setForm({
          ...defaultsForTemplate(template.id),
          ...defaultsToFields(nextConfig || {}),
          ...saved,
          report_no: cert.certification_search_id || saved.report_no || "",
          issue_date: cert.issue_date || saved.issue_date || "",
          expire_date: cert.expire_date || saved.expire_date || "",
          id_no: saved.id_no || cert.person_id_no || "",
        });
        setLoadingEdit(false);
      }
    };
    load();
  }, [token, editIdParam]);

  const verifyUrl = `${publicSiteUrl.replace(/\/+$/, "")}/?id=${encodeURIComponent(form.report_no || "")}`;

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(verifyUrl, { margin: 1, width: 160, errorCorrectionLevel: "M" })
      .then((url) => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => { if (!cancelled) setQrDataUrl(""); });
    return () => { cancelled = true; };
  }, [verifyUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleComponentChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      component_rows: prev.component_rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const addComponentRow = () => {
    setForm((prev) => ({ ...prev, component_rows: [...prev.component_rows, { ...emptyRow }] }));
  };

  const removeComponentRow = (index) => {
    setForm((prev) => ({
      ...prev,
      component_rows: prev.component_rows.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleCylinderChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      cylinder_rows: prev.cylinder_rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const addCylinderRow = () => {
    setForm((prev) => ({
      ...prev,
      cylinder_rows: [...(prev.cylinder_rows || []), emptyCylinderRow()],
    }));
  };

  const removeCylinderRow = (index) => {
    setForm((prev) => ({
      ...prev,
      cylinder_rows: prev.cylinder_rows.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleEmissionChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      emission_rows: prev.emission_rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const addEmissionRow = () => {
    setForm((prev) => ({
      ...prev,
      emission_rows: [...(prev.emission_rows || []), emptyEmissionRow()],
    }));
  };

  const removeEmissionRow = (index) => {
    setForm((prev) => ({
      ...prev,
      emission_rows: prev.emission_rows.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleAccessoryChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      accessory_rows: prev.accessory_rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const addAccessoryRow = () => {
    setForm((prev) => ({
      ...prev,
      accessory_rows: [...(prev.accessory_rows || []), emptyAccessoryRow()],
    }));
  };

  const removeAccessoryRow = (index) => {
    setForm((prev) => ({
      ...prev,
      accessory_rows: prev.accessory_rows.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleTestResultChange = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      test_result_rows: prev.test_result_rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      ),
    }));
  };

  const addTestResultRow = () => {
    setForm((prev) => ({
      ...prev,
      test_result_rows: [...(prev.test_result_rows || []), emptyTestResultRow()],
    }));
  };

  const removeTestResultRow = (index) => {
    setForm((prev) => ({
      ...prev,
      test_result_rows: prev.test_result_rows.filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const handleAssetCreated = (asset) => setAssets((prev) => [...prev, asset]);

  const selectSignature = (idField, nameField) => (asset) => {
    setForm((prev) => ({
      ...prev,
      [idField]: asset ? asset.id : "",
      [nameField]: asset ? asset.name : prev[nameField],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTemplate) return;
    setLoading(true);
    setError("");
    setMessage("");

    const res = await generateCertificateAPI({
      ...form,
      ...(editingId ? { id: editingId } : {}),
      ...(isCrane ? {
        issue_date: form.date_of_issuance || form.issue_date,
        expire_date: form.date_of_validity || form.expire_date,
        report_date: form.date_of_inspection || form.report_date,
      } : {}),
      ...(isEmission ? {
        issue_date: form.date_of_test || form.issue_date,
        expire_date: form.calibration_validity || form.expire_date,
      } : {}),
      ...(isWastewater ? {
        issue_date: form.report_date || form.issue_date,
      } : {}),
      ...(isCraneOperator ? {
        inspected_by_name: form.trained_by_name || form.inspected_by_name,
      } : {}),
      certification_search_id: form.report_no.trim(),
      template_id: selectedTemplate.id,
    }, token);

    setLoading(false);
    if (res.success) {
      if (editingId) {
        setMessage("Certificate updated. Old PDF removed and a new PDF was generated.");
        setTimeout(() => navigate("/all-certifications"), 800);
      } else {
        setMessage("Certificate generated and added to All Certifications.");
        setForm({ ...defaultsForTemplate(selectedTemplate.id), ...defaultsToFields({ ...config, assets }) });
        setSelectedTemplate(null);
      }
    } else {
      setError(res.message || "Failed to generate certificate.");
    }
  };

  const assetsBase = selectedTemplate?.assets_base_url || "";
  const inspectedSignature = assetById(form.inspected_by_signature_id);
  const approvedSignature = assetById(form.approved_by_signature_id);
  const companySeal = assetById(form.company_seal_id);
  const examPhoto = assetById(form.exam_photo_id);

  const uploadExamPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    setError("");
    const res = await createCertificateAssetAPI({
      asset_type: "photo",
      name: isCraneOperator
        ? `Operator photo ${form.report_no || "card"}`
        : `Equipment photo ${form.report_no || "crane"}`,
      file,
    }, token);
    setUploadingPhoto(false);
    if (res.success) {
      handleAssetCreated(res.asset);
      setForm((prev) => ({ ...prev, exam_photo_id: res.asset.id }));
    } else {
      setError(res.message || "Failed to upload photo.");
    }
  };

  // Switching saved footers replaces the logos, websites and contact lines;
  // they can still be adjusted for this certificate only.
  const selectFooter = (id) => {
    const footer = footers.find((item) => String(item.id) === String(id));
    if (footer) setForm((prev) => ({ ...prev, ...footerFields(footer) }));
  };

  const footerEditorValue = {
    companies: form.footer_companies,
    website: form.footer_website,
    email: form.footer_email,
    gmail: form.footer_gmail,
  };

  const patchFooter = (patch) => {
    setForm((prev) => ({
      ...prev,
      ...(patch.companies !== undefined ? { footer_companies: patch.companies } : {}),
      ...(patch.website !== undefined ? { footer_website: patch.website } : {}),
      ...(patch.email !== undefined ? { footer_email: patch.email } : {}),
      ...(patch.gmail !== undefined ? { footer_gmail: patch.gmail } : {}),
    }));
  };

  // Persist the logos/contacts currently on this form back to the selected
  // saved footer so the same change is available on every later certificate.
  const saveFooterChanges = async () => {
    if (!form.footer_id) {
      setError("Pick a saved footer before saving.");
      return;
    }
    const selected = footers.find((item) => String(item.id) === String(form.footer_id));
    setSavingFooter(true);
    setError("");
    const res = await saveCertificateFooterAPI({
      id: form.footer_id,
      name: selected?.name || "Footer",
      companies: form.footer_companies,
      website: form.footer_website,
      email: form.footer_email,
      gmail: form.footer_gmail,
    }, token);
    setSavingFooter(false);
    if (res.success) {
      setFooters((prev) => prev.map((footer) => (footer.id === res.footer.id ? res.footer : footer)));
      setMessage(`Footer "${res.footer.name}" saved. New certificates that use it will show these logos and contacts.`);
    } else {
      setError(res.message || "Failed to save footer.");
    }
  };

  const previewDoc = {
    ...form,
    template_id: selectedTemplate?.id,
    assetsBase,
    inspectedSignatureUrl: inspectedSignature?.image_url || "",
    approvedSignatureUrl: approvedSignature?.image_url || "",
    sealUrl: companySeal?.image_url || "",
    examPhotoUrl: examPhoto?.image_url || "",
    qrDataUrl,
    footerLogos: footerToLogos(form.footer_companies, footerAssets),
  };

  const openFilledPreview = () => {
    localStorage.setItem(PREVIEW_STORAGE_KEY, JSON.stringify(previewDoc));
    window.open("/certificate-preview", "_blank", "noopener");
  };

  const textField = (name, label, extra = {}) => (
    <Form.Group className="mb-3">
      <Form.Label className="cp-form-label">{label}</Form.Label>
      <Form.Control className="cp-form-control" name={name} value={form[name]} onChange={handleChange} {...extra} />
    </Form.Group>
  );

  return (
    <DashboardLayout pageTitle={editingId ? "Edit Certificate" : "Create Certificate"}>
      <div className="cp-header">
        <div>
          <h2 className="cp-header__title">{editingId ? "Edit Certificate" : "Create Certificate"}</h2>
          <p className="cp-header__sub">
            {editingId
              ? "Update details and regenerate the PDF. The previous PDF file is deleted when you save."
              : "Select a template, fill details, preview, and generate a certificate."}
          </p>
        </div>
        {editingId && (
          <button type="button" className="cp-modal-cancel" onClick={() => navigate("/all-certifications")}>
            <BsArrowLeft /> All Certifications
          </button>
        )}
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      {loadingEdit ? (
        <div className="cp-loading">
          <Spinner animation="border" style={{ color: "var(--blue)" }} />
          <span>Loading certificate…</span>
        </div>
      ) : !selectedTemplate ? (
        <div className="ac-form-card">
          <h3 className="db-card__title">Certificate Templates</h3>
          <div className="cc-template-list">
            {templates.map((template) => (
              <button type="button" key={template.id} className="cc-template" onClick={() => openTemplate(template)}>
                <BsFileEarmarkPdf />
                <span>{template.name}</span>
              </button>
            ))}
            {!templates.length && !error && <Spinner animation="border" size="sm" />}
          </div>
        </div>
      ) : (
        <div className="cc-grid">
          <div className="ac-form-card">
            {!editingId && (
              <button type="button" className="cp-modal-cancel mb-3" onClick={() => setSelectedTemplate(null)}>
                <BsArrowLeft /> Templates
              </button>
            )}
            <h3 className="db-card__title">{selectedTemplate.name}</h3>

            <Form onSubmit={handleSubmit} className="mt-4">
              <section className="cc-part cc-part--header">
                <div className="cc-part__head">
                  <span className="cc-part__num">1</span>
                  <div>
                    <h4 className="cc-part__title">Header</h4>
                    <p className="cc-part__hint">
                      {isCraneOperator
                        ? "Card header: Certificate No., qualification title, and issue / expiry dates."
                        : isWastewater
                        ? "Top of the page: report title, company, Report No., project, dates and location."
                        : isLifting
                          ? "Top of the page: register title, Certificate No., client, job and inspection place."
                          : isEmission
                            ? "Top of the page: report title, equipment type, Report No., client and equipment fields."
                            : isFm200
                              ? "Top of the page: report title, CERT NO., company, client, dates and Inspected By."
                              : isCrane
                                ? "Top of the page: boxed report title and the Report Reference / Client table."
                                : "Top of the page: report title and the Project / Report No. / Client table."}
                    </p>
                  </div>
                </div>
                <div className="cc-part__body">
                  <Row>
                    <Col md={4}>{textField("issue_date", isEmission ? "Date of Test / Issue" : isWastewater ? "Report Date / Issue" : "Issue Date", { type: "date", required: true })}</Col>
                    <Col md={4}>{textField("expire_date", isFm200 || isEmission || isLifting || isCraneOperator ? "Valid Till / Expiry" : "Expiry Date", { type: "date", required: true })}</Col>
                    <Col md={4}>{textField("report_title", isCraneOperator ? "Qualification Title" : "Report Title")}</Col>
                  </Row>
                  {isCraneOperator ? (
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="cp-form-label">Certificate No. <span className="cp-req">*</span></Form.Label>
                          <Form.Control className="cp-form-control" name="report_no" value={form.report_no} onChange={handleChange} required />
                          <Form.Text muted>Printed in red on the card and encoded in the QR code for public search.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>{textField("level", "Level")}</Col>
                      <Col md={4}>{textField("company", "Company")}</Col>
                      <Col md={4}>{textField("project", "Project")}</Col>
                    </Row>
                  ) : isWastewater ? (
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="cp-form-label">Report No. <span className="cp-req">*</span></Form.Label>
                          <Form.Control className="cp-form-control" name="report_no" value={form.report_no} onChange={handleChange} required />
                          <Form.Text muted>Used as the unique search ID and encoded in the QR code.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>{textField("company_name", "Company Name")}</Col>
                      <Col md={4}>{textField("project", "Project")}</Col>
                      <Col md={4}>{textField("client", "Client")}</Col>
                      <Col md={4}>{textField("location", "Location")}</Col>
                      <Col md={4}>{textField("date_of_sampling", "Date of Sampling", { type: "date" })}</Col>
                      <Col md={4}>{textField("date_of_testing", "Date of Testing", { type: "date" })}</Col>
                      <Col md={4}>{textField("report_date", "Report Date", { type: "date" })}</Col>
                    </Row>
                  ) : isLifting ? (
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="cp-form-label">Certificate No. <span className="cp-req">*</span></Form.Label>
                          <Form.Control className="cp-form-control" name="report_no" value={form.report_no} onChange={handleChange} required />
                          <Form.Text muted>Used as the unique search ID and encoded in the QR code.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>{textField("client", "Client")}</Col>
                      <Col md={4}>{textField("job_no", "Job No.")}</Col>
                      <Col md={4}>{textField("place_of_inspection", "Place of Inspection")}</Col>
                      <Col md={4}>{textField("standard_specification", "Standard/Specification")}</Col>
                      <Col md={4}>{textField("registration_no", "Registration No.")}</Col>
                    </Row>
                  ) : isEmission ? (
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="cp-form-label">Report No. <span className="cp-req">*</span></Form.Label>
                          <Form.Control className="cp-form-control" name="report_no" value={form.report_no} onChange={handleChange} required />
                          <Form.Text muted>Used as the unique search ID and encoded in the QR code.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>{textField("equipment_type", "Equipment type (subtitle)")}</Col>
                      <Col md={4}>{textField("client", "Client")}</Col>
                      <Col md={4}>{textField("model", "Model")}</Col>
                      <Col md={4}>{textField("serial_no", "Sr. No.")}</Col>
                      <Col md={4}>{textField("manufacturer", "Manufacturer/Make")}</Col>
                      <Col md={3}>{textField("capacity", "Capacity")}</Col>
                      <Col md={3}>{textField("nature_of_power", "Nature of Power")}</Col>
                      <Col md={3}>{textField("fuel_type", "Fuel Type")}</Col>
                      <Col md={3}>{textField("emission_standard", "Emission Standard")}</Col>
                      <Col md={4}>{textField("location", "Location")}</Col>
                      <Col md={4}>{textField("date_of_test", "Date of Test", { type: "date" })}</Col>
                      <Col md={4}>{textField("ambient_temperature", "Ambient Temperature")}</Col>
                      <Col md={4}>{textField("humidity", "Humidity")}</Col>
                    </Row>
                  ) : isFm200 ? (
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="cp-form-label">CERT NO. <span className="cp-req">*</span></Form.Label>
                          <Form.Control className="cp-form-control" name="report_no" value={form.report_no} onChange={handleChange} required />
                          <Form.Text muted>Used as the unique search ID and encoded in the QR code.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>{textField("company", "Company")}</Col>
                      <Col md={4}>{textField("client", "Client")}</Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="cp-form-label">Inspected by</Form.Label>
                          <Form.Control className="cp-form-control" value={form.inspected_by_name} readOnly />
                          <Form.Text muted>Same person as Sign-off → Inspected By below.</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  ) : isCrane ? (
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="cp-form-label">Report Reference <span className="cp-req">*</span></Form.Label>
                          <Form.Control className="cp-form-control" name="report_no" value={form.report_no} onChange={handleChange} required />
                          <Form.Text muted>Used as the unique search ID and encoded in the QR code.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={4}>{textField("activity", "Activity")}</Col>
                      <Col md={4}>{textField("client", "Client")}</Col>
                      <Col md={4}>{textField("location", "Location of Inspection")}</Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label className="cp-form-label">Inspected by</Form.Label>
                          <Form.Control className="cp-form-control" value={form.inspected_by_name} readOnly />
                          <Form.Text muted>Same person as Sign-off → Inspected By below.</Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  ) : (
                    <Row>
                      <Col md={3}>{textField("project", "Project")}</Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="cp-form-label">Report No. <span className="cp-req">*</span></Form.Label>
                          <Form.Control className="cp-form-control" name="report_no" value={form.report_no} onChange={handleChange} required />
                          <Form.Text muted>Used as the unique search ID and encoded in the QR code.</Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={3}>{textField("location", "Location")}</Col>
                      <Col md={3}>{textField("report_date", "Date", { type: "date" })}</Col>
                      <Col md={3}>{textField("client", "Client")}</Col>
                      <Col md={3}>{textField("contractor", "Contractor")}</Col>
                      <Col md={3}>{textField("structure", "Structure")}</Col>
                      <Col md={3}>{textField("revision", "Rev.")}</Col>
                    </Row>
                  )}
                </div>
              </section>

              <section className="cc-part cc-part--body">
                <div className="cc-part__head">
                  <span className="cc-part__num">2</span>
                  <div>
                    <h4 className="cc-part__title">Body</h4>
                    <p className="cc-part__hint">
                      {isCraneOperator
                        ? "Operator photo, holder details, trained / approved by, QR and seal."
                        : isWastewater
                        ? "Sample information, test results table, compliance statement, signatures and seal."
                        : isLifting
                          ? "Summary of inspection table (add/remove rows), standard, conclusion, remarks, signatures and seal."
                          : isEmission
                            ? "Methodology, emission results table, test equipment, remarks, signatures and seal. No disclaimer on this template."
                            : isFm200
                              ? "Cylinder inspection table (add/remove rows), note, signatures and seal. No disclaimer on this template."
                              : isCrane
                                ? "Equipment details, scope of survey, photo, dates, signatures and seal."
                                : "Middle of the page: report sections, component table, conclusion, signatures and seal."}
                    </p>
                  </div>
                </div>
                <div className="cc-part__body">
                  {isCraneOperator ? (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label className="cp-form-label">Operator photo</Form.Label>
                        <div className="cc-exam-upload cc-exam-upload--portrait">
                          {examPhoto?.image_url ? (
                            <img src={examPhoto.image_url} alt="Operator" className="cc-exam-upload__preview cc-exam-upload__preview--portrait" />
                          ) : (
                            <div className="cc-exam-upload__placeholder">
                              <span>ID portrait</span>
                              <small>35 × 45 ratio</small>
                            </div>
                          )}
                          <div>
                            <label className="cp-edit-btn cc-logo-upload">
                              {uploadingPhoto ? <Spinner size="sm" animation="border" /> : <><BsUpload size={13} /> {examPhoto ? "Replace photo" : "Upload photo"}</>}
                              <input
                                type="file"
                                accept=".png,.jpg,.jpeg"
                                hidden
                                disabled={uploadingPhoto}
                                onChange={(e) => { uploadExamPhoto(e.target.files?.[0]); e.target.value = ""; }}
                              />
                            </label>
                            <Form.Text muted className="d-block mt-1">
                              Use a clear passport-style portrait (head &amp; shoulders). Cropped to standard ID size on the card.
                            </Form.Text>
                          </div>
                        </div>
                      </Form.Group>
                      <Row>
                        <Col md={6}>{textField("holder_name", "Name")}</Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="cp-form-label">ID No. (CNIC)</Form.Label>
                            <Form.Control className="cp-form-control" name="id_no" value={form.id_no} onChange={handleChange} />
                            <Form.Text muted>Shown as ID NO. on the card. Also searchable on the public verify page.</Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>{textField("trained_by_name", "Trained By")}</Col>
                        <Col md={6}>{textField("approved_by_name", "Approved By")}</Col>
                      </Row>
                      <Form.Text muted className="d-block mb-3">
                        Seal and QR appear on the card. Accreditation footer logos are not used on this square card template.
                      </Form.Text>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label className="cp-form-label">QR Code</Form.Label>
                            <div className="cc-qr-field">
                              {qrDataUrl && <img src={qrDataUrl} alt="QR" />}
                              <small>{verifyUrl}</small>
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <CertificateAssetPicker
                            label="Company Seal"
                            assetType="seal"
                            assets={seals}
                            value={form.company_seal_id}
                            onChange={(asset) => setForm((prev) => ({ ...prev, company_seal_id: asset ? asset.id : "" }))}
                            onAssetCreated={handleAssetCreated}
                            token={token}
                          />
                        </Col>
                      </Row>
                    </>
                  ) : isWastewater ? (
                    <>
                      <h6 className="cc-form-section">Sample Information</h6>
                      <Row>
                        <Col md={4}>{textField("sample_id", "Sample ID")}</Col>
                        <Col md={4}>{textField("sample_type", "Sample Type")}</Col>
                        <Col md={4}>{textField("sampling_location", "Sampling Location")}</Col>
                        <Col md={4}>{textField("sampling_method", "Sampling Method")}</Col>
                        <Col md={4}>{textField("sample_condition", "Sample Condition")}</Col>
                        <Col md={4}>{textField("sampling_datetime", "Date & Time of Sampling")}</Col>
                      </Row>
                      <div className="d-flex justify-content-between align-items-center mb-2 mt-2">
                        <Form.Label className="cp-form-label mb-0">Test Results</Form.Label>
                        <button type="button" className="cp-edit-btn" onClick={addTestResultRow}><BsPlus size={13} /> Add Row</button>
                      </div>
                      {(form.test_result_rows || []).map((row, index) => (
                        <div key={index} className="cc-fm200-row">
                          <div className="cc-fm200-row__head">
                            <strong>Parameter {index + 1}</strong>
                            <button
                              type="button"
                              className="cp-delete-btn"
                              title="Remove row"
                              onClick={() => removeTestResultRow(index)}
                              disabled={(form.test_result_rows || []).length === 1}
                            >
                              <BsTrash size={13} />
                            </button>
                          </div>
                          <Row>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Parameter</Form.Label>
                                <Form.Control className="cp-form-control" value={row.parameter} onChange={(e) => handleTestResultChange(index, "parameter", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Test Method / Standard</Form.Label>
                                <Form.Control className="cp-form-control" value={row.test_method} onChange={(e) => handleTestResultChange(index, "test_method", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Unit</Form.Label>
                                <Form.Control className="cp-form-control" value={row.unit} onChange={(e) => handleTestResultChange(index, "unit", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Result</Form.Label>
                                <Form.Control className="cp-form-control" value={row.result} onChange={(e) => handleTestResultChange(index, "result", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Applicable Limit</Form.Label>
                                <Form.Control className="cp-form-control" value={row.applicable_limit} onChange={(e) => handleTestResultChange(index, "applicable_limit", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Compliance</Form.Label>
                                <Form.Control className="cp-form-control" value={row.compliance} onChange={(e) => handleTestResultChange(index, "compliance", e.target.value)} />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      ))}
                      {textField("compliance_statement", "Compliance Statement", { as: "textarea", rows: 3 })}
                    </>
                  ) : isLifting ? (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="cp-form-label mb-0">Summary of Inspection</Form.Label>
                        <button type="button" className="cp-edit-btn" onClick={addAccessoryRow}><BsPlus size={13} /> Add Row</button>
                      </div>
                      {(form.accessory_rows || []).map((row, index) => (
                        <div key={index} className="cc-fm200-row">
                          <div className="cc-fm200-row__head">
                            <strong>Item {index + 1}</strong>
                            <button
                              type="button"
                              className="cp-delete-btn"
                              title="Remove row"
                              onClick={() => removeAccessoryRow(index)}
                              disabled={(form.accessory_rows || []).length === 1}
                            >
                              <BsTrash size={13} />
                            </button>
                          </div>
                          <Row>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Description</Form.Label>
                                <Form.Control className="cp-form-control" value={row.description} onChange={(e) => handleAccessoryChange(index, "description", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">WLL / SWL</Form.Label>
                                <Form.Control className="cp-form-control" value={row.wll_swl} onChange={(e) => handleAccessoryChange(index, "wll_swl", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Date of Examination</Form.Label>
                                <Form.Control className="cp-form-control" type="date" value={row.date_of_examination} onChange={(e) => handleAccessoryChange(index, "date_of_examination", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Next Examination</Form.Label>
                                <Form.Control className="cp-form-control" type="date" value={row.date_of_next_examination} onChange={(e) => handleAccessoryChange(index, "date_of_next_examination", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Type of Inspection</Form.Label>
                                <Form.Control className="cp-form-control" value={row.type_of_inspection} onChange={(e) => handleAccessoryChange(index, "type_of_inspection", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Identification / Tag No.</Form.Label>
                                <Form.Control className="cp-form-control" value={row.identification} onChange={(e) => handleAccessoryChange(index, "identification", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Qty.</Form.Label>
                                <Form.Control className="cp-form-control" value={row.qty} onChange={(e) => handleAccessoryChange(index, "qty", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Safe to Use</Form.Label>
                                <Form.Control className="cp-form-control" value={row.safe_to_use} onChange={(e) => handleAccessoryChange(index, "safe_to_use", e.target.value)} />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      ))}
                      {textField("inspection_standard", "Inspection Standard")}
                      {textField("conclusion_remarks", "Conclusion & Remarks", { as: "textarea", rows: 2 })}
                      {textField("remarks", "Remarks", { as: "textarea", rows: 2 })}
                    </>
                  ) : isEmission ? (
                    <>
                      {textField("test_methodology", "Test Methodology", { as: "textarea", rows: 2 })}
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="cp-form-label mb-0">Emission Measurement Results</Form.Label>
                        <button type="button" className="cp-edit-btn" onClick={addEmissionRow}><BsPlus size={13} /> Add Row</button>
                      </div>
                      <div className="cc-rows-head cc-emit-rows-head d-none d-md-grid">
                        <span>Parameter</span><span>Unit</span><span>Methodology</span><span>Test Result</span><span>NEQS</span><span />
                      </div>
                      {(form.emission_rows || []).map((row, index) => (
                        <div key={index} className="cc-row cc-emit-row">
                          <Form.Control className="cp-form-control" placeholder="Parameter" value={row.parameter} onChange={(e) => handleEmissionChange(index, "parameter", e.target.value)} />
                          <Form.Control className="cp-form-control" placeholder="Unit" value={row.unit} onChange={(e) => handleEmissionChange(index, "unit", e.target.value)} />
                          <Form.Control className="cp-form-control" placeholder="Methodology" value={row.methodology} onChange={(e) => handleEmissionChange(index, "methodology", e.target.value)} />
                          <Form.Control className="cp-form-control" placeholder="Test Result" value={row.test_result} onChange={(e) => handleEmissionChange(index, "test_result", e.target.value)} />
                          <Form.Control className="cp-form-control" placeholder="NEQS" value={row.neqs} onChange={(e) => handleEmissionChange(index, "neqs", e.target.value)} />
                          <button type="button" className="cp-delete-btn" title="Remove row" onClick={() => removeEmissionRow(index)} disabled={(form.emission_rows || []).length === 1}>
                            <BsTrash size={13} />
                          </button>
                        </div>
                      ))}
                      <h6 className="cc-form-section mt-3">Test Equipment Details</h6>
                      <Row>
                        <Col md={6}>{textField("smoke_meter", "Smoke Meter Used")}</Col>
                        <Col md={3}>{textField("calibration_date", "Calibration Date", { type: "date" })}</Col>
                        <Col md={3}>{textField("calibration_validity", "Validity", { type: "date" })}</Col>
                        <Col md={6}>{textField("meter_serial_no", "Meter Serial No.")}</Col>
                      </Row>
                      {textField("remarks_conclusion", "Remarks and Conclusion", { as: "textarea", rows: 3 })}
                    </>
                  ) : isFm200 ? (
                    <>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="cp-form-label mb-0">Inspection Summary — cylinders</Form.Label>
                        <button type="button" className="cp-edit-btn" onClick={addCylinderRow}><BsPlus size={13} /> Add Row</button>
                      </div>
                      {(form.cylinder_rows || []).map((row, index) => (
                        <div key={index} className="cc-fm200-row">
                          <div className="cc-fm200-row__head">
                            <strong>Cylinder {index + 1}</strong>
                            <button
                              type="button"
                              className="cp-delete-btn"
                              title="Remove row"
                              onClick={() => removeCylinderRow(index)}
                              disabled={(form.cylinder_rows || []).length === 1}
                            >
                              <BsTrash size={13} />
                            </button>
                          </div>
                          <Row>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Identification No.</Form.Label>
                                <Form.Control className="cp-form-control" value={row.identification_no} onChange={(e) => handleCylinderChange(index, "identification_no", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">ICC/DOT Spec.</Form.Label>
                                <Form.Control className="cp-form-control" value={row.icc_dot_spec} onChange={(e) => handleCylinderChange(index, "icc_dot_spec", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">MFG</Form.Label>
                                <Form.Control className="cp-form-control" value={row.mfg} onChange={(e) => handleCylinderChange(index, "mfg", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Date of MFG</Form.Label>
                                <Form.Control className="cp-form-control" value={row.date_of_mfg} onChange={(e) => handleCylinderChange(index, "date_of_mfg", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Size/T-Weight</Form.Label>
                                <Form.Control className="cp-form-control" value={row.size_t_weight} onChange={(e) => handleCylinderChange(index, "size_t_weight", e.target.value)} />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Form.Label className="cp-form-label">Shell inspection (checked = OK)</Form.Label>
                          <div className="cc-fm200-shells">
                            {FM200_SHELL_KEYS.map((key, i) => (
                              <Form.Check
                                key={key}
                                type="checkbox"
                                id={`cyl-${index}-${key}`}
                                label={FM200_SHELL_LABELS[i]}
                                checked={!!row[key]}
                                onChange={(e) => handleCylinderChange(index, key, e.target.checked)}
                              />
                            ))}
                          </div>
                          <Row className="mt-2">
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Disposition</Form.Label>
                                <Form.Control className="cp-form-control" value={row.disposition} onChange={(e) => handleCylinderChange(index, "disposition", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={3}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Inspection Date</Form.Label>
                                <Form.Control className="cp-form-control" type="date" value={row.inspection_date} onChange={(e) => handleCylinderChange(index, "inspection_date", e.target.value)} />
                              </Form.Group>
                            </Col>
                            <Col md={6}>
                              <Form.Group className="mb-2">
                                <Form.Label className="cp-form-label">Remarks</Form.Label>
                                <Form.Control className="cp-form-control" value={row.remarks} onChange={(e) => handleCylinderChange(index, "remarks", e.target.value)} />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      ))}
                      {textField("note", "Note", { as: "textarea", rows: 3 })}
                    </>
                  ) : isCrane ? (
                    <>
                      <Row>
                        <Col md={6}>{textField("description_of_supply", "Description of Supply")}</Col>
                        <Col md={3}>{textField("make_model", "Make/Model")}</Col>
                        <Col md={3}>{textField("serial_no", "Serial No/Chassis")}</Col>
                        <Col md={3}>{textField("registration_number", "Registration Number")}</Col>
                        <Col md={3}>{textField("capacity", "Capacity")}</Col>
                        <Col md={3}>{textField("type_of_operation", "Type of Operation")}</Col>
                        <Col md={3}>{textField("scope_of_survey", "Scope of Survey (short)")}</Col>
                      </Row>
                      {textField("scope_of_survey_detail", "Scope of Survey (detail)", { as: "textarea", rows: 6 })}
                      <Form.Group className="mb-3">
                        <Form.Label className="cp-form-label">Equipment photo</Form.Label>
                        <div className="cc-exam-upload">
                          {examPhoto?.image_url ? (
                            <img src={examPhoto.image_url} alt="Equipment" className="cc-exam-upload__preview" />
                          ) : (
                            <span className="text-muted">Optional — left cell stays blank with the brand watermark if empty.</span>
                          )}
                          <label className="cp-edit-btn cc-logo-upload">
                            {uploadingPhoto ? <Spinner size="sm" animation="border" /> : <><BsUpload size={13} /> {examPhoto ? "Replace photo" : "Upload photo"}</>}
                            <input
                              type="file"
                              accept=".png,.jpg,.jpeg"
                              hidden
                              disabled={uploadingPhoto}
                              onChange={(e) => { uploadExamPhoto(e.target.files?.[0]); e.target.value = ""; }}
                            />
                          </label>
                        </div>
                      </Form.Group>
                      <Row>
                        <Col md={4}>{textField("operation_test", "Operation Test")}</Col>
                        <Col md={4}>{textField("date_of_issuance", "Date of Issuance", { type: "date" })}</Col>
                        <Col md={4}>{textField("date_of_validity", "Date of Validity", { type: "date" })}</Col>
                        <Col md={4}>{textField("date_of_inspection", "Date of Inspection", { type: "date" })}</Col>
                        <Col md={4}>{textField("reference_code", "Reference code & standard")}</Col>
                        <Col md={4}>{textField("recommendations", "Recommendations / Subjected to")}</Col>
                      </Row>
                    </>
                  ) : (
                    <>
                      <Form.Text muted className="d-block mb-2">Wrap text in double asterisks to make it bold, e.g. <code>**2.35 kN**</code>.</Form.Text>
                      {textField("purpose_scope", "Purpose & Scope", { as: "textarea", rows: 3 })}
                      {textField("proposed_suspension_arrangement", "Proposed Suspension Arrangement", { as: "textarea", rows: 2 })}
                      {textField("load_calculation", "Load Calculation", { as: "textarea", rows: 2 })}

                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <Form.Label className="cp-form-label mb-0">Component Verification</Form.Label>
                        <button type="button" className="cp-edit-btn" onClick={addComponentRow}><BsPlus size={13} /> Add Row</button>
                      </div>
                      <div className="cc-rows-head d-none d-md-grid">
                        <span>Component</span><span>Assessment Item</span><span>Requirement</span><span>WLL/SWL ≥ Design Load</span><span>Status</span><span />
                      </div>
                      {(form.component_rows || []).map((row, index) => (
                        <div key={index} className="cc-row">
                          <Form.Control as="textarea" rows={2} className="cp-form-control" placeholder="Component" value={row.component} onChange={(e) => handleComponentChange(index, "component", e.target.value)} />
                          <Form.Control as="textarea" rows={2} className="cp-form-control" placeholder="Assessment Item" value={row.assessment_item} onChange={(e) => handleComponentChange(index, "assessment_item", e.target.value)} />
                          <Form.Control as="textarea" rows={2} className="cp-form-control" placeholder="Requirement" value={row.requirement} onChange={(e) => handleComponentChange(index, "requirement", e.target.value)} />
                          <Form.Control as="textarea" rows={2} className="cp-form-control" placeholder="WLL/SWL ≥ Design Load" value={row.design_load} onChange={(e) => handleComponentChange(index, "design_load", e.target.value)} />
                          <Form.Control as="textarea" rows={2} className="cp-form-control" placeholder="Status" value={row.status} onChange={(e) => handleComponentChange(index, "status", e.target.value)} />
                          <button type="button" className="cp-delete-btn" title="Remove row" onClick={() => removeComponentRow(index)} disabled={(form.component_rows || []).length === 1}>
                            <BsTrash size={13} />
                          </button>
                        </div>
                      ))}

                      <div className="mt-3">
                        {textField("final_assessment", "Final Assessment / Conclusion", { as: "textarea", rows: 6 })}
                      </div>
                    </>
                  )}

                  {!isCraneOperator && (
                  <>
                  <h6 className="cc-form-section">Sign-off</h6>
                  <Form.Text muted className="d-block mb-2">
                    Pre-filled from the saved defaults. <Link to="/certificate-configuration"><BsSliders size={12} /> Configuration</Link> changes the defaults for all templates.
                    {(isCrane || isFm200 || isEmission) && " Inspected By maps to the sign-off block."}
                  </Form.Text>
                  <Row>
                    <Col md={4}>
                      {textField("inspected_by_name", "Inspected By (name)")}
                      <CertificateAssetPicker
                        label="Inspected By signature"
                        assetType="signature"
                        assets={signatures}
                        value={form.inspected_by_signature_id}
                        onChange={selectSignature("inspected_by_signature_id", "inspected_by_name")}
                        onAssetCreated={handleAssetCreated}
                        token={token}
                        allowDraw
                      />
                    </Col>
                    <Col md={4}>
                      {textField("approved_by_name", "Approved By (name)")}
                      <CertificateAssetPicker
                        label="Approved By signature"
                        assetType="signature"
                        assets={signatures}
                        value={form.approved_by_signature_id}
                        onChange={selectSignature("approved_by_signature_id", "approved_by_name")}
                        onAssetCreated={handleAssetCreated}
                        token={token}
                        allowDraw
                      />
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="cp-form-label">QR Code</Form.Label>
                        <div className="cc-qr-field">
                          {qrDataUrl && <img src={qrDataUrl} alt="QR" />}
                          <small>{verifyUrl}</small>
                        </div>
                      </Form.Group>
                      <CertificateAssetPicker
                        label="Company Seal"
                        assetType="seal"
                        assets={seals}
                        value={form.company_seal_id}
                        onChange={(asset) => setForm((prev) => ({ ...prev, company_seal_id: asset ? asset.id : "" }))}
                        onAssetCreated={handleAssetCreated}
                        token={token}
                      />
                    </Col>
                  </Row>
                  </>
                  )}
                </div>
              </section>

              {!isCardTemplate && (
              <section className="cc-part cc-part--footer">
                <div className="cc-part__head">
                  <span className="cc-part__num">3</span>
                  <div>
                    <h4 className="cc-part__title">Footer</h4>
                    <p className="cc-part__hint">
                      {noDisclaimer
                        ? "Bottom of the page: the seven company logos and their websites, plus the contact lines. This template has no disclaimer."
                        : "Bottom of the page: disclaimer, the seven company logos and their websites, plus the contact lines."}
                    </p>
                  </div>
                </div>
                <div className="cc-part__body">
              {!noDisclaimer && (
                <>
                  {textField("disclaimer_text", "Disclaimer text", { as: "textarea", rows: 3 })}
                  {textField("end_of_report_text", "End of report line")}
                </>
              )}
              <Row className="align-items-end">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="cp-form-label">Saved footer</Form.Label>
                    <Form.Select className="cp-form-control" value={form.footer_id || ""} onChange={(e) => selectFooter(e.target.value)}>
                      {!footers.length && <option value="">Default footer</option>}
                      {footers.map((footer) => (
                        <option key={footer.id} value={footer.id}>
                          {footer.name}{String(footer.id) === String(config?.defaults?.footer_id) ? " (default)" : ""}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text muted>
                      Pick a saved footer, then edit logos or contacts below. Save writes those changes back to the same footer. You can also do this in <Link to="/certificate-configuration">Configuration</Link>.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <button type="button" className="cp-edit-btn" onClick={saveFooterChanges} disabled={savingFooter || !form.footer_id}>
                    {savingFooter ? <Spinner size="sm" animation="border" /> : <><BsSave size={13} /> Save footer</>}
                  </button>
                </Col>
              </Row>
              <FooterEditor
                value={footerEditorValue}
                onChange={patchFooter}
                footerAssets={footerAssets}
                token={token}
                onAssetCreated={handleAssetCreated}
                onError={setError}
              />
                </div>
              </section>
              )}

              <button className="cp-modal-submit" type="submit" disabled={loading || !selectedTemplate}>
                {loading ? <Spinner size="sm" animation="border" /> : editingId ? <><BsSave /> Save &amp; Regenerate PDF</> : <><BsPlus /> Generate Certificate</>}
              </button>
            </Form>
          </div>

          <div className="ac-form-card">
            <h3 className="db-card__title">Live Preview</h3>
            <div className={`cc-preview${isCraneOperator ? " cc-preview--card" : ""}`}>
              <CertificateDocument doc={previewDoc} />
            </div>
            <button type="button" className="cp-file-pdf mt-3" onClick={openFilledPreview}>
              <BsBoxArrowUpRight /> Open filled certificate
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CreateCertificatePage;
